import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import ImageWrapper from "../ImageComponent/ImageWrapper";
import {createCRMLeadEnquiryBody, getCookieItem} from "@/utils/utils";
import { postGlobalLeadCapture} from "@/pages/api";
import {useRouter} from "next/router";

const MainModal = ({ isOpen, onClose, modalData, poojaDetails, userData }) => {
  const router = useRouter();
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    pujaType: poojaDetails?.name || "",
    name: "",
    phone: "",
    email: "",
    notes: "",
  });

  const modalRef = useRef();
  const accessToken = getCookieItem("accessToken");
  const isLoggedIn = !!accessToken;

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Update puja type when poojaDetails changes
  useEffect(() => {
    if (poojaDetails?.name) {
      setFormData(prev => ({ ...prev, pujaType: poojaDetails.name }));
    }
  }, [poojaDetails]);

  if (!isOpen) return null;

  // Validation functions
  const validatePhone = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone) ? "" : "Please enter a valid 10-digit phone number";
  };

  const validateEmail = (email) => {
    if (!email) return ""; // Email is optional
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    return emailRegex.test(email) ? "" : "Please enter a valid email address";
  };

  const validateForm = () => {
    const newErrors = {};

    // For logged-in users, only notes is required
    if (isLoggedIn) {
      if (!formData.notes.trim()) {
        newErrors.notes = "Please add your comments";
      }
    } else {
      // For non-logged-in users, name, phone, and notes are required
      if (!formData.name.trim()) {
        newErrors.name = "Name is required";
      }
      if (!formData.phone.trim()) {
        newErrors.phone = "Phone number is required";
      } else {
        const phoneError = validatePhone(formData.phone);
        if (phoneError) newErrors.phone = phoneError;
      }
      if (!formData.notes.trim()) {
        newErrors.notes = "Please add your comments";
      }
    }

    // Email validation (optional field)
    if (formData.email.trim()) {
      const emailError = validateEmail(formData.email);
      if (emailError) newErrors.email = emailError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    // For phone, only allow numeric input
    if (field === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }

    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear specific field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      city: '',
      pujaType: '',
      date: '',
      dateSelection: '',
      notes: ''
    });
  };
  // Submit form
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the errors below");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = createCRMLeadEnquiryBody({...formData, enquiry: formData?.notes});
      await postGlobalLeadCapture({
        ...payload,
        source: "Website-Pooja",
        webPageLink: process.env.NEXT_PUBLIC_SITE_URL+router.asPath
      });
      setTimeout(() => {
        onClose();
        setIsSubmitted(false);
        toast("Your enquiry has been submitted successfully!");
        resetForm();
      }, 500);
    } catch (err) {
      console.error("Error from baserow:", err.message);
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleClose = () => {
    setErrors({});
    setIsSubmitted(false);
    setIsSubmitting(false);
    onClose();
  };

  return (
      <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black bg-opacity-50">
        <div
            ref={modalRef}
            className="flex flex-col w-full h-full md:h-auto md:w-536 bg-white rounded-t-2xl md:rounded-2xl max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex-shrink-0">
            <div className="flex justify-between items-center px-4 md:px-6 py-4">
              <h3 className="font-medium text-base text-black">
                {modalData?.modalTitle || "Book Pooja"}
              </h3>
              <button
                  onClick={handleClose}
                  className="w-6 h-6 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
              >
                <ImageWrapper
                    src="/icons/Cancelbutton.svg"
                    alt="Close"
                    className="w-5 h-5"
                />
              </button>
            </div>
            <hr className="border-gray-300" />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6">
            {isSubmitted ? (
                // Success State
                <div className="flex flex-col items-center justify-center text-center py-8">
                  <h1 className="text-red-600 font-bold text-2xl mb-2">
                    Book My Pooja
                  </h1>
                  <h4 className="text-lg font-medium mb-1">
                    Your sacred wish received
                  </h4>
                  <p className="text-gray-600 text-sm">
                    The BookMyPooja team will get in touch with you soon
                  </p>
                </div>
            ) : (
                // Form State
                <div className="space-y-4">
                  {/* Pooja Details Card */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl">
                    <div className="w-12 h-12 flex-shrink-0 rounded-md overflow-hidden bg-gray-200">
                      <ImageWrapper
                          src={poojaDetails?.media?.[0]?.mediaUrl || "/placeholder-pooja.jpg"}
                          alt={poojaDetails?.name || "Pooja"}
                          className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm text-black mb-1">
                        {poojaDetails?.name || "Pooja Name"}
                      </h4>
                      <p className="text-xs text-gray-600 line-clamp-2">
                        {poojaDetails?.shortDescription || "Pooja description"}
                      </p>
                    </div>
                  </div>

                  {/* Form Fields - Only show name, phone, email for non-logged-in users */}
                      <>
                        {/* Name Field */}
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Full Name *
                          </label>
                          <div className="relative">
                            <input
                                type="text"
                                placeholder="Enter your full name"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                className={`w-full pl-2 pr-3 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                                    errors.name ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                          </div>
                          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>

                        {/* Phone Field */}
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Mobile Number *
                          </label>
                          <div className="relative">
                            <input
                                type="tel"
                                placeholder="Enter 10-digit mobile number"
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                maxLength={10}
                                className={`w-full pl-2 pr-3 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                                    errors.phone ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                          </div>
                          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                        </div>

                        {/* Email Field */}
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">
                            Email (Optional)
                          </label>
                          <div className="relative">
                            <input
                                type="email"
                                placeholder="Enter your email address"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                className={`w-full pl-2 pr-3 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                                    errors.email ? 'border-red-500' : 'border-gray-300'
                                }`}
                            />
                          </div>
                          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>
                      </>

                  {/* Notes Field */}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      Your Comments *
                    </label>
                    <textarea
                        placeholder="Please share your requirements..."
                        value={formData.notes}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        rows={4}
                        className={`w-full px-3 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 resize-none ${
                            errors.notes ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {errors.notes && <p className="text-red-500 text-xs mt-1">{errors.notes}</p>}
                  </div>
                </div>
            )}
          </div>

          {/* Footer */}
          {!isSubmitted && (
              <div className="flex-shrink-0 border-t border-gray-300 px-4 md:px-6 py-4">
                <div className="flex justify-end">
                  <button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className={`px-6 py-3 rounded-lg font-medium text-white transition-colors ${
                          isSubmitting
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-BluntRed hover:bg-red-700 active:bg-red-800'
                      }`}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </div>
          )}
        </div>
      </div>
  );
};

export default MainModal;